<template>
  <div class="animated fadeIn">

    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="icon-plus mr-2 text-danger"></i>Create New Issue</h4>
        <small class="text-muted">Log a new support issue and assign it to the team</small>
      </b-col>
      <b-col class="text-right">
        <b-button variant="outline-secondary" size="sm" to="/issue-tracker/list">
          <i class="icon-arrow-left mr-1"></i>Back to List
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <!-- Form -->
      <b-col md="8" class="mb-4">
        <b-card class="shadow-sm">
          <b-form @submit.prevent="submit">

            <b-form-group label="Issue Title *" label-class="fw-600">
              <b-form-input v-model.trim="form.title" placeholder="Short, clear description of the issue" required />
            </b-form-group>

            <b-form-group label="System / Server" description="Which Tipsoi instance is this issue for? Sets the client type automatically.">
              <multiselect
                v-model="selectedSystem"
                :options="systems"
                :loading="systemsLoading"
                :custom-label="systemLabel"
                track-by="code"
                label="name"
                placeholder="Search a system — Live HRM, Live BEP CS, APEX CS…"
                :allow-empty="true"
                :show-labels="false"
                @input="onSystemSelect"
              >
                <span slot="noResult">No system matches.</span>
                <span slot="noOptions">No systems configured.</span>
              </multiselect>
            </b-form-group>

            <b-form-row>
              <b-col md="6">
                <b-form-group label="Client Type *" label-class="fw-600">
                  <b-form-select v-model="form.clientType" :options="clientTypeOptions" required />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Category *" label-class="fw-600">
                  <b-form-select v-model="form.category" :options="categoryOptions" required />
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col md="6">
                <b-form-group label="Issue Type *" label-class="fw-600"
                              description="Is this about the application, a physical device, or something else?">
                  <b-form-select v-model="form.issueType" :options="issueTypeOptions" required />
                </b-form-group>
              </b-col>
            </b-form-row>

            <!-- CS = Central Server (Tipsoi Device Portal): searchable Project picker (central-server project list, same as Create Office) -->
            <b-form-row v-if="showProjectPicker">
              <b-col md="12">
                <b-form-group label="Client (Central Server Project)" description="Search and select the Tipsoi Device Portal project — fills Client ID + Name automatically">
                  <multiselect
                    v-model="selectedProject"
                    :options="projects"
                    :loading="projectsLoading"
                    :internal-search="true"
                    :custom-label="projectLabel"
                    track-by="id"
                    label="name"
                    placeholder="Type to search project…"
                    :allow-empty="true"
                    :show-labels="false"
                    @input="onProjectSelect"
                  >
                    <span slot="noResult">No project matches your search.</span>
                    <span slot="noOptions">No projects loaded.</span>
                  </multiselect>
                  <small v-if="selectedProject" class="text-muted">
                    Selected project ID: <strong>{{ form.clientId }}</strong>
                  </small>
                </b-form-group>
              </b-col>
            </b-form-row>

            <!-- HRM (or device scoping): searchable office picker + optional device picker -->
            <template v-else-if="showOfficePicker">
              <b-form-row>
                <b-col md="12">
                  <b-form-group label="Client (HRM Office)" description="Search and select the client office from HRM — fills Client ID + Name automatically">
                    <multiselect
                      v-model="selectedOffice"
                      :options="offices"
                      :loading="officesLoading"
                      :internal-search="true"
                      :custom-label="officeLabel"
                      track-by="id"
                      label="name"
                      placeholder="Type to search office / company…"
                      :allow-empty="true"
                      :show-labels="false"
                      @input="onOfficeSelect"
                    >
                      <span slot="noResult">No office matches your search.</span>
                      <span slot="noOptions">No offices loaded.</span>
                    </multiselect>
                    <small v-if="form.clientId" class="text-muted">
                      Selected office ID: <strong>{{ form.clientId }}</strong>
                    </small>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <!-- Device picker: only for a DEVICE issue once an office is chosen -->
              <b-form-row v-if="showDevicePicker">
                <b-col md="12">
                  <b-form-group label="Device" description="Which device is this issue about? (devices of the selected office)">
                    <multiselect
                      v-model="selectedDevice"
                      :options="devices"
                      :loading="devicesLoading"
                      :internal-search="true"
                      :custom-label="deviceLabel"
                      track-by="id"
                      label="identifier"
                      placeholder="Type to search device…"
                      :allow-empty="true"
                      :show-labels="false"
                      @input="onDeviceSelect"
                    >
                      <span slot="noResult">No device matches your search.</span>
                      <span slot="noOptions">This office has no active devices.</span>
                    </multiselect>
                    <small v-if="form.deviceIdentifier" class="text-muted">
                      Selected device: <strong>{{ form.deviceIdentifier }}</strong>
                    </small>
                  </b-form-group>
                </b-col>
              </b-form-row>
            </template>

            <!-- Remote / non-local, non-CS system selected: client is the system itself -->
            <b-alert v-else-if="showSystemNote" show variant="light" class="border small mb-3">
              <i class="fa fa-server mr-1 text-primary"></i>
              This issue will be logged against <strong>{{ selectedSystem.name }}</strong>
              (<code>{{ selectedSystem.code }}</code>).
              <span class="text-muted">Picking a specific office/device on this system will be available once its API is wired.</span>
            </b-alert>

            <!-- Fallback: free-text -->
            <b-form-row v-else>
              <b-col md="6">
                <b-form-group label="Client ID" description="Office ID, Project ID, or reference">
                  <b-form-input v-model.trim="form.clientId" placeholder="e.g. OFFICE-101" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Client Name" description="Company or office name">
                  <b-form-input v-model.trim="form.clientName" placeholder="e.g. ABC Corporation" />
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col md="6">
                <b-form-group label="Priority *" label-class="fw-600">
                  <b-form-select v-model="form.priority" :options="priorityOptions" required />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Assign To" description="Select a support team member">
                  <b-form-select v-model="form.assignedMemberId" :options="memberOptions">
                    <template #first>
                      <b-form-select-option :value="null">— Unassigned —</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-group label="Due Date" description="Optional deadline">
              <b-form-input v-model="form.dueDate" type="datetime-local" />
            </b-form-group>

            <b-form-group label="Description" description="Reproduce steps, environment, screenshots info…">
              <b-form-textarea v-model="form.description" rows="5"
                               placeholder="Describe the issue in detail…" />
            </b-form-group>

            <div class="d-flex mt-3">
              <b-button type="submit" variant="danger" :disabled="submitting" class="mr-2 px-4">
                <b-spinner v-if="submitting" small class="mr-1" />
                {{ submitting ? 'Creating…' : 'Create Issue' }}
              </b-button>
              <b-button type="button" variant="outline-secondary" to="/issue-tracker/list">
                Cancel
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>

      <!-- Right Guide -->
      <b-col md="4" class="mb-4">
        <b-card header="Priority Guide" class="shadow-sm mb-3">
          <b-list-group flush>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="danger" class="mr-2 mt-1" style="min-width:70px;text-align:center">CRITICAL</b-badge>
                <small class="text-muted">System down, data loss, affecting many users. Requires immediate action.</small>
              </div>
            </b-list-group-item>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="warning" class="mr-2 mt-1" style="min-width:70px;text-align:center">HIGH</b-badge>
                <small class="text-muted">Major feature broken, blocking one client's workflow.</small>
              </div>
            </b-list-group-item>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="info" class="mr-2 mt-1" style="min-width:70px;text-align:center">MEDIUM</b-badge>
                <small class="text-muted">Feature partially working, workaround available.</small>
              </div>
            </b-list-group-item>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="secondary" class="mr-2 mt-1" style="min-width:70px;text-align:center">LOW</b-badge>
                <small class="text-muted">Minor UI issue, enhancement request, nice-to-have.</small>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card>

        <b-card header="Client Types" class="shadow-sm">
          <b-list-group flush>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="primary" class="mr-2">HRM</b-badge>
              <small class="text-muted">HRM application client issues</small>
            </b-list-group-item>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="success" class="mr-2">CS</b-badge>
              <small class="text-muted">Central Server (Tipsoi Device Portal) issues</small>
            </b-list-group-item>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge class="bg-purple text-white mr-2">ADMIN</b-badge>
              <small class="text-muted">Admin panel internal issues</small>
            </b-list-group-item>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="warning" class="mr-2">OTHER</b-badge>
              <small class="text-muted">Other platforms or external</small>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import issueService from '@/services/issueService'
import teamMemberService from '@/services/teamMemberService'
import officeService from '@/services/officeService'
import customerService from '@/services/customerService'
import issueConfigService from '@/services/issueConfigService'
import Multiselect from 'vue-multiselect'

export default {
  name: 'IssueCreate',
  components: { Multiselect },
  data: function() {
    return {
      submitting: false,
      memberOptions: [],
      offices: [],
      officesLoading: false,
      selectedOffice: null,
      projects: [],
      projectsLoading: false,
      selectedProject: null,
      devices: [],
      devicesLoading: false,
      selectedDevice: null,
      loadedDeviceOfficeId: null,   // cache guard — avoids re-fetching the same office's devices
      systems: [],
      systemsLoading: false,
      selectedSystem: null,
      form: {
        title: '', description: '', clientType: 'HRM', clientId: '',
        clientName: '', priority: 'MEDIUM', category: 'SUPPORT',
        issueType: 'HRM', deviceId: '', deviceIdentifier: '',
        assignedMemberId: null, dueDate: ''
      },
      clientTypeOptions: [
        { value: 'HRM',   text: 'HRM' },
        { value: 'CS',    text: 'CS' },
        { value: 'ADMIN', text: 'Admin' },
        { value: 'OTHER', text: 'Other' },
      ],
      priorityOptions: [
        { value: 'CRITICAL', text: 'Critical' },
        { value: 'HIGH',     text: 'High' },
        { value: 'MEDIUM',   text: 'Medium' },
        { value: 'LOW',      text: 'Low' },
      ],
      categoryOptions: [
        { value: 'BUG',             text: 'Bug' },
        { value: 'FEATURE_REQUEST', text: 'Feature Request' },
        { value: 'SUPPORT',         text: 'Support' },
        { value: 'BILLING',         text: 'Billing' },
        { value: 'PERFORMANCE',     text: 'Performance' },
        { value: 'OTHER',           text: 'Other' },
      ],
      issueTypeOptions: [
        { value: 'HRM',    text: 'HRM (application / software)' },
        { value: 'DEVICE', text: 'Device (hardware)' },
        { value: 'OTHER',  text: 'Other' },
      ]
    }
  },
  computed: {
    // No system chosen, or the chosen system is the one this panel talks to directly.
    isLocalSystem: function() {
      return !this.selectedSystem || this.selectedSystem.local
    },
    // CS → Project picker. Projects come from the central server, so this works for any CS system.
    showProjectPicker: function() {
      return this.form.clientType === 'CS'
    },
    // HRM → Office picker (local system). Also shown to scope devices for a DEVICE issue.
    showOfficePicker: function() {
      if (this.showProjectPicker) { return false }
      if (!this.isLocalSystem) { return false }
      return this.form.clientType === 'HRM' || this.form.issueType === 'DEVICE'
    },
    // Device dropdown: a DEVICE issue on a local system, once an office is resolved.
    showDevicePicker: function() {
      return this.form.issueType === 'DEVICE' && this.isLocalSystem && !!this.selectedOffice
    },
    // A remote (non-local), non-CS system is selected — client is the system itself.
    showSystemNote: function() {
      return !!this.selectedSystem && !this.selectedSystem.local && !this.showProjectPicker
    }
  },
  watch: {
    // Load the right client list for the chosen type; clear the pickers that no longer apply.
    // Does NOT clear clientId/clientName — those are driven by the system/office/project handlers.
    'form.clientType': function(newType) {
      if (newType === 'HRM') {
        if (this.offices.length === 0) { this.loadOffices() }
      } else {
        this.selectedOffice = null
      }
      if (newType === 'CS') {
        this.loadProjects()
      } else {
        this.selectedProject = null
      }
    },
    // Toggle device scoping. Only fetch devices lazily, and only when an office is already picked.
    'form.issueType': function(newType) {
      if (newType === 'DEVICE') {
        if (this.selectedOffice) { this.loadDevices(this.selectedOffice.id) }
      } else {
        this.clearDeviceSelection()
      }
    }
  },
  created: function() {
    this.loadMembers()
    this.loadSystems()
    if (this.form.clientType === 'HRM') { this.loadOffices() }
  },
  methods: {
    loadSystems: function() {
      var self = this
      self.systemsLoading = true
      issueConfigService.getEnabledServers(
        function(list) { self.systems = list || []; self.systemsLoading = false },
        function() { self.systemsLoading = false }
      )
    },
    systemLabel: function(s) {
      if (!s) { return '' }
      return s.name + ' (' + s.category + ')'
    },
    onSystemSelect: function(sys) {
      this.selectedOffice = null
      this.selectedProject = null
      this.clearDeviceSelection()
      if (sys) {
        this.form.clientType = sys.category || 'OTHER'   // watcher loads offices (HRM) / projects (CS)
        this.form.clientId   = sys.code
        this.form.clientName = sys.name
      } else {
        this.form.clientId   = ''
        this.form.clientName = ''
      }
    },
    loadOffices: function() {
      var self = this
      self.officesLoading = true
      officeService.getOfficeList(
        function(list) { self.offices = list || []; self.officesLoading = false },
        function() {
          self.officesLoading = false
          self.$bvToast.toast('Could not load the HRM office list.', { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    officeLabel: function(o) {
      if (!o) { return '' }
      return o.name + ' (#' + o.id + ')'
    },
    onOfficeSelect: function(o) {
      // Office changed → any device chosen for the previous office is no longer valid.
      this.clearDeviceSelection()
      if (o) {
        this.form.clientId = String(o.id)
        this.form.clientName = this.selectedSystem ? (this.selectedSystem.name + ' — ' + o.name) : o.name
        if (this.form.issueType === 'DEVICE') { this.loadDevices(o.id) }
      } else if (this.selectedSystem) {
        // revert to the system defaults
        this.form.clientId = this.selectedSystem.code
        this.form.clientName = this.selectedSystem.name
      } else {
        this.form.clientId = ''
        this.form.clientName = ''
      }
    },
    // ── CS Project picker ──────────────────────────────────────────────────
    loadProjects: function() {
      if (this.projects.length > 0 || this.projectsLoading) { return }  // load once, cache
      var self = this
      self.projectsLoading = true
      customerService.getProjectNames(
        function(list) { self.projects = list || []; self.projectsLoading = false },
        function() {
          self.projectsLoading = false
          self.$bvToast.toast('Could not load the project list.', { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    projectLabel: function(p) {
      if (!p) { return '' }
      return p.organization ? (p.name + ' — ' + p.organization) : p.name
    },
    onProjectSelect: function(p) {
      if (p) {
        this.form.clientId = String(p.id)
        this.form.clientName = this.selectedSystem ? (this.selectedSystem.name + ' — ' + p.name) : p.name
      } else if (this.selectedSystem) {
        this.form.clientId = this.selectedSystem.code
        this.form.clientName = this.selectedSystem.name
      } else {
        this.form.clientId = ''
        this.form.clientName = ''
      }
    },
    // ── Device picker (scoped to the chosen office) ────────────────────────
    loadDevices: function(officeId) {
      if (!officeId) { return }
      // cache guard: same office already loaded → don't hit the client DB again
      if (this.loadedDeviceOfficeId === officeId && this.devices.length > 0) { return }
      var self = this
      self.devicesLoading = true
      self.devices = []
      officeService.getDevicesOfAnOffice(
        officeId,
        function(list) {
          self.devices = list || []
          self.devicesLoading = false
          self.loadedDeviceOfficeId = officeId
        },
        function() {
          self.devicesLoading = false
          self.$bvToast.toast('Could not load devices for this office.', { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    deviceLabel: function(d) {
      return d ? d.identifier : ''
    },
    onDeviceSelect: function(d) {
      if (d) {
        this.form.deviceId = String(d.id)
        this.form.deviceIdentifier = d.identifier
      } else {
        this.form.deviceId = ''
        this.form.deviceIdentifier = ''
      }
    },
    clearDeviceSelection: function() {
      this.selectedDevice = null
      this.devices = []
      this.loadedDeviceOfficeId = null
      this.form.deviceId = ''
      this.form.deviceIdentifier = ''
    },
    loadMembers: function() {
      var self = this
      teamMemberService.listActive(
        function(data) {
          self.memberOptions = (data || []).map(function(m) {
            var label = m.email ? (m.full_name + ' (' + m.email + ')') : m.full_name
            return { value: m.id, text: label }
          })
        },
        function() {}
      )
    },
    submit: function() {
      if (!this.form.title || !this.form.clientType || !this.form.priority) {
        this.$bvToast.toast('Please fill in all required fields.', { title: 'Validation', variant: 'warning', solid: true })
        return
      }
      var self = this
      self.submitting = true
      issueService.create(
        self.form,
        function(data) {
          self.submitting = false
          self.$bvToast.toast('Issue #' + data.id + ' created successfully.', { title: 'Created', variant: 'success', solid: true })
          setTimeout(function() { self.$router.push('/issue-tracker/list') }, 1200)
        },
        function(err) {
          self.submitting = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    }
  }
}
</script>

<style scoped>
.fw-600 { font-weight: 600; }
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
